/**
 * Narrows the site's active-alarm list to one device and buckets it for the
 * SLD device card.
 *
 * Fetches the whole site once and filters client-side (design doc §4): the list
 * endpoint has no device_id filter, and a shared cached list makes switching
 * between diagram nodes instant. Note this does NOT share a cache entry with
 * the Alarms tab — that surface passes a different `status` filter, and the
 * filter is part of the query key.
 */
import { useMemo } from 'react';
import { useSiteAlarms } from '@/hooks/useSiteAlarms';
import type { ActiveAlarm } from '@/types/alarm.types';
import { isDtcCondition, parseDtcAlarm, type DtcParseResult } from '../utils/dtc';

export interface DeviceConditions {
  alarms: ActiveAlarm[];
  warnings: ActiveAlarm[];
  dtcRows: ActiveAlarm[];
  dtc: DtcParseResult;
  /** `last_ts` of the DTC row — the age of the code list. Design doc §7.5. */
  dtcAsOf: string | null;
  isLoading: boolean;
  isError: boolean;
  dataUpdatedAt: number;
}

const EMPTY_DTC: DtcParseResult = { entries: [], declaredCount: null, rawFallback: null };

/**
 * `Date.parse` returns NaN for an unparseable timestamp. Used directly in a
 * sort comparator's subtraction, that NaN propagates into the comparator's
 * return value, which is unspecified/implementation-dependent — NOT
 * guaranteed to rank the malformed row last. Clamp to the oldest possible
 * instant instead, so a malformed `last_ts` can never win "newest" and two
 * malformed rows compare equal (0) rather than NaN.
 */
function parseTsMs(iso: string): number {
  const ms = Date.parse(iso);
  return Number.isNaN(ms) ? Number.MIN_SAFE_INTEGER : ms;
}

export function bucketDeviceConditions(
  rows: ActiveAlarm[],
  deviceId: string | undefined,
): { alarms: ActiveAlarm[]; warnings: ActiveAlarm[]; dtcRows: ActiveAlarm[] } {
  if (!deviceId) return { alarms: [], warnings: [], dtcRows: [] };

  // C2: an SLD node's `data.deviceId` is the telemetry join key, which for
  // some nodes (e.g. a BESS tile) is a block id, not a roster device id —
  // Hondo's seeded BESS node carries deviceId "ES-E3E0", while alarm rows
  // for the BMS/PCS devices inside that block carry device_id "BMS-001" /
  // "PCS-001" with block_id "ES-E3E0" (database/seed/sld-hondo.sql). Match
  // on either identity so a block-keyed node still picks up its constituent
  // devices' rows. For a generator, block_id === device_id, so this is a
  // no-op there.
  const mine = rows
    // Cleared rows must never render as active conditions. The server is asked
    // for uncleared rows only, but this filter is deliberately NOT trusting
    // that: the hook's status contract has already changed twice (#434, #628),
    // and `serverStatusFilter` collapses a multi-bucket selection to
    // `undefined`, which the backend reads as "no cleared filter at all". A
    // future caller passing two buckets would silently start feeding cleared
    // alarms onto this card. One predicate here makes that impossible.
    .filter((r) => !r.cleared_at)
    // Acknowledged rows are excluded too (#684). The SLD panel answers "what
    // needs attention on this device right now"; an acked row is one an operator
    // has already taken ownership of, and its full lifecycle — including
    // history — belongs on the Alarms tab. This is why Hondo's panel listed 7
    // ALARMS with `ACK` on every one while the site had nothing unacknowledged.
    // Note the server-side `status: ['active']` filter does NOT do this: the
    // legacy `active` bucket means `cleared_at IS NULL`, acked included.
    .filter((r) => !r.acked_at)
    .filter((r) => r.device_id === deviceId || r.block_id === deviceId);

  // DTC rows are extracted BEFORE the severity split. gen.dtc.code is
  // severity 'warning', so a severity-first split double-renders it.
  const dtcRows = mine.filter((r) => isDtcCondition(r.condition_key));
  const rest = mine.filter((r) => !isDtcCondition(r.condition_key));

  return {
    dtcRows,
    alarms: rest.filter((r) => r.severity === 'critical'),
    // info rows are dropped — the reconciler already routes them to
    // state_events, so this is defence in depth, not load-bearing.
    warnings: rest.filter((r) => r.severity === 'warning'),
  };
}

export function useDeviceConditions(
  siteId: string | undefined,
  deviceId: string | undefined,
): DeviceConditions {
  // `['active']` — a SINGLE-element array, deliberately. `serverStatusFilter`
  // only forwards a status to the server when exactly one bucket is selected;
  // a multi-element array collapses to `undefined`, which `listActiveForSite`
  // treats as "apply no cleared filter", so the payload would then include
  // cleared rows. The legacy `active` bucket maps to `cleared_at IS NULL`
  // (alarm-query.service.ts) — a SUPERSET of what this panel renders, since
  // `bucketDeviceConditions` additionally drops acked rows client-side (#684).
  // Keep the query shape identical to `useDeviceAlarmSeverity`'s so the panel
  // and the diagram badges share one cache entry. See #628 for the signature
  // change.
  const query = useSiteAlarms(siteId ?? '', { statuses: ['active'] });

  return useMemo(() => {
    const rows = query.data?.alarms ?? [];
    const { alarms, warnings, dtcRows } = bucketDeviceConditions(rows, deviceId);

    // All of a device's active DTCs arrive on a single row; if several DTC
    // rows are present, the newest wins. `parseTsMs` clamps an unparseable
    // last_ts to the oldest possible instant instead of leaving Date.parse's
    // NaN in the subtraction — a NaN comparator result is unspecified sort
    // behavior, not "defers to the other row", so a single malformed
    // timestamp could otherwise win the "newest" pick outright.
    const primary = dtcRows
      .slice()
      .sort((a, b) => parseTsMs(b.last_ts) - parseTsMs(a.last_ts))[0];

    return {
      alarms,
      warnings,
      dtcRows,
      dtc: primary ? parseDtcAlarm(primary) : EMPTY_DTC,
      dtcAsOf: primary?.last_ts ?? null,
      isLoading: query.isLoading,
      isError: query.isError,
      dataUpdatedAt: query.dataUpdatedAt,
    };
  }, [query.data, query.isLoading, query.isError, query.dataUpdatedAt, deviceId]);
}
